import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';

import { useNavigate } from 'react-router-dom';
import { useError } from '../contexts/ErrorContext';
import { vpnApi, VPNSessionListResponse } from '../api/vpn';
import { formatBytes, formatDuration, formatDate } from '../utils/formatting';
import { extractErrorMessage } from '../utils/errorHandling';
import {
  ChartBarIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ClockIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  UserGroupIcon,
  ServerIcon
} from '@heroicons/react/24/outline';

interface UsageStats {
  totalSessions: number;
  totalUpload: number;
  totalDownload: number;
  totalDuration: number;
  averageSessionDuration: number;
  uniqueFriends: number;
  sessionsHosted: number;
  sessionsConsumed: number;
}

const Usage: React.FC = () => {
  const navigate = useNavigate();
  const { addError } = useError();

  const [sessions, setSessions] = useState<VPNSessionListResponse | null>(null);
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeFilter, setTimeFilter] = useState<'7d' | '30d' | 'all'>('30d');

  const calculateStats = useCallback((sessionData: VPNSessionListResponse) => {
    const allSessions = [...sessionData.hosted_sessions, ...sessionData.client_sessions];

    // Filter by time period
    const now = new Date();
    const cutoffDate = new Date();
    if (timeFilter === '7d') {
      cutoffDate.setDate(now.getDate() - 7);
    } else if (timeFilter === '30d') {
      cutoffDate.setDate(now.getDate() - 30);
    } else {
      cutoffDate.setFullYear(2000); // Include all sessions
    }

    const filteredSessions = allSessions.filter(session =>
      new Date(session.created_at) >= cutoffDate
    );

    const totalUpload = filteredSessions.reduce((sum, session) => sum + session.bytes_uploaded, 0);
    const totalDownload = filteredSessions.reduce((sum, session) => sum + session.bytes_downloaded, 0);
    const totalDuration = filteredSessions.reduce((sum, session) => sum + session.duration_seconds, 0);

    const uniqueFriends = new Set([
      ...sessionData.hosted_sessions.map(s => s.client_display_name),
      ...sessionData.client_sessions.map(s => s.host_display_name)
    ]).size;

    setStats({
      totalSessions: filteredSessions.length,
      totalUpload,
      totalDownload,
      totalDuration,
      averageSessionDuration: filteredSessions.length > 0 ? totalDuration / filteredSessions.length : 0,
      uniqueFriends,
      sessionsHosted: sessionData.hosted_sessions.filter(s =>
        new Date(s.created_at) >= cutoffDate
      ).length,
      sessionsConsumed: sessionData.client_sessions.filter(s =>
        new Date(s.created_at) >= cutoffDate
      ).length,
    });
  }, [timeFilter]);

  const fetchUsageData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await vpnApi.getSessions();
      setSessions(response.data);
      calculateStats(response.data);
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to fetch usage data');
      addError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [addError, calculateStats]);

  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    await fetchUsageData();
    setIsRefreshing(false);
  }, [fetchUsageData]);

  useEffect(() => {
    fetchUsageData();
  }, [timeFilter, fetchUsageData]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-primary-800';
      case 'disconnected':
        return 'bg-gray-100 text-gray-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center mr-4 text-gray-500 hover:text-gray-700"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-1" />
                Back to Dashboard
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Usage Statistics</h1>
                <p className="text-sm text-gray-600 mt-1">Monitor your VPN usage and activity</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value as '7d' | '30d' | 'all')}
                className="border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="all">All time</option>
              </select>
              <button
                onClick={refreshData}
                disabled={isRefreshing}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 space-y-8">
        {/* Summary Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Sessions
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stats.totalSessions}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ArrowUpIcon className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Data Uploaded
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {formatBytes(stats.totalUpload)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ArrowDownIcon className="h-6 w-6 text-purple-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Data Downloaded
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {formatBytes(stats.totalDownload)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-6 w-6 text-orange-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Duration
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {formatDuration(stats.totalDuration)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Additional Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <UserGroupIcon className="h-8 w-8 text-primary-500" />
                <div className="ml-4">
                  <div className="text-sm font-medium text-gray-500">Unique Friends</div>
                  <div className="text-2xl font-bold text-gray-900">{stats.uniqueFriends}</div>
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <ServerIcon className="h-8 w-8 text-green-500" />
                <div className="ml-4">
                  <div className="text-sm font-medium text-gray-500">Sessions Hosted</div>
                  <div className="text-2xl font-bold text-gray-900">{stats.sessionsHosted}</div>
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <ClockIcon className="h-8 w-8 text-purple-500" />
                <div className="ml-4">
                  <div className="text-sm font-medium text-gray-500">Avg Session Duration</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatDuration(Math.round(stats.averageSessionDuration))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Session History */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Session History
              </h3>
              <span className="text-sm text-gray-500">
                {timeFilter === '7d' ? 'Last 7 days' : timeFilter === '30d' ? 'Last 30 days' : 'All time'}
              </span>
            </div>

            {!sessions || (sessions.hosted_sessions.length === 0 && sessions.client_sessions.length === 0) ? (
              <div className="text-center py-8">
                <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No sessions found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Start using VPN connections to see usage statistics here.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Hosted Sessions */}
                {sessions.hosted_sessions.length > 0 && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                      <ServerIcon className="h-5 w-5 text-green-500 mr-2" />
                      Sessions You Hosted ({sessions.hosted_sessions.length})
                    </h4>
                    <div className="space-y-3">
                      {sessions.hosted_sessions.map((session) => (
                        <div key={session.session_id} className="border border-green-200 bg-green-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-sm font-medium text-green-800">
                                    {session.client_display_name}
                                  </div>
                                  <div className="text-xs text-green-600">
                                    {session.session_name && `${session.session_name} • `}
                                    {formatDate(session.created_at)}
                                  </div>
                                </div>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(session.status)}`}>
                                  {session.status}
                                </span>
                              </div>
                              <div className="mt-2 grid grid-cols-3 gap-4 text-xs text-green-600">
                                <div>
                                  <span className="font-medium">Duration:</span> {formatDuration(session.duration_seconds)}
                                </div>
                                <div>
                                  <span className="font-medium">↑ Upload:</span> {formatBytes(session.bytes_uploaded)}
                                </div>
                                <div>
                                  <span className="font-medium">↓ Download:</span> {formatBytes(session.bytes_downloaded)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Client Sessions */}
                {sessions.client_sessions.length > 0 && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                      <UserGroupIcon className="h-5 w-5 text-primary-500 mr-2" />
                      Sessions You Joined ({sessions.client_sessions.length})
                    </h4>
                    <div className="space-y-3">
                      {sessions.client_sessions.map((session) => (
                        <div key={session.session_id} className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-sm font-medium text-primary-800">
                                    {session.host_display_name}
                                  </div>
                                  <div className="text-xs text-primary-600">
                                    {session.session_name && `${session.session_name} • `}
                                    {formatDate(session.created_at)}
                                  </div>
                                </div>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(session.status)}`}>
                                  {session.status}
                                </span>
                              </div>
                              <div className="mt-2 grid grid-cols-3 gap-4 text-xs text-primary-600">
                                <div>
                                  <span className="font-medium">Duration:</span> {formatDuration(session.duration_seconds)}
                                </div>
                                <div>
                                  <span className="font-medium">↑ Upload:</span> {formatBytes(session.bytes_uploaded)}
                                </div>
                                <div>
                                  <span className="font-medium">↓ Download:</span> {formatBytes(session.bytes_downloaded)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </AppLayout>
  );
};

export default Usage;
